import React, { useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Link,
  useTheme
} from '@mui/material';
import { Shield as ShieldIcon } from '@mui/icons-material';
import axios from 'axios';
// import { useAuth } from '../contexts/AuthContext';
import NeuraStrikeLogo from '../components/NeuraStrikeLogo';

const Verify2FA: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // const { login } = useAuth();

  // Get email and password from location state or URL params
  const email = location.state?.email || searchParams.get('email') || '';
  // const password = location.state?.password || '';

  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resending, setResending] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Verify 2FA code - this will return tokens if successful
      const response = await axios.post('/api/auth/verify-2fa', { email, code });

      if (response.data.tokens) {
        // Store tokens
        localStorage.setItem('token', response.data.tokens.access_token);
        localStorage.setItem('refreshToken', response.data.tokens.refresh_token);
        localStorage.setItem('user', JSON.stringify(response.data.user));

        // Redirect to dashboard
        window.location.href = '/dashboard';
      } else {
        setError('Login failed after 2FA verification');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setResending(true);
    setError('');

    try {
      await axios.post('/api/auth/send-2fa-code', { email });
      setError('');
      alert('New code sent to your email!');
    } catch (err: any) {
      setError('Failed to resend code');
    } finally {
      setResending(false);
    }
  };

  if (!email) {
    navigate('/login');
    return null;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background:
          theme.palette.mode === 'dark'
            ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'
            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            theme.palette.mode === 'dark'
              ? `
              radial-gradient(circle at 20% 50%, rgba(99, 102, 241, 0.15) 0%, transparent 50%),
              radial-gradient(circle at 80% 80%, rgba(236, 72, 153, 0.15) 0%, transparent 50%)
            `
              : `
              radial-gradient(circle at 20% 50%, rgba(99, 102, 241, 0.3) 0%, transparent 50%),
              radial-gradient(circle at 80% 80%, rgba(236, 72, 153, 0.3) 0%, transparent 50%)
            `,
          animation: 'pulse 10s ease-in-out infinite'
        },
        '@keyframes pulse': {
          '0%, 100%': { opacity: 1 },
          '50%': { opacity: 0.8 }
        }
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={3}
          sx={{
            p: 5,
            borderRadius: 4,
            background:
              theme.palette.mode === 'dark'
                ? 'rgba(30, 41, 59, 0.98)'
                : 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            border:
              theme.palette.mode === 'dark'
                ? '1px solid rgba(51, 65, 85, 0.5)'
                : '1px solid rgba(255, 255, 255, 0.5)',
            position: 'relative',
            zIndex: 1
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
              <NeuraStrikeLogo height={48} />
            </Box>
            <ShieldIcon sx={{ fontSize: 60, color: '#6366f1', mb: 2 }} />
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 800,
                background: 'linear-gradient(135deg, #6366f1 0%, #ec4899 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1
              }}
            >
              Two-Factor Authentication
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Enter the 6-digit code sent to your email
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {email}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              required
              fullWidth
              id="code"
              label="Verification Code"
              name="code"
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              sx={{ mb: 3 }}
              inputProps={{
                maxLength: 6,
                style: { textAlign: 'center', fontSize: '24px', letterSpacing: '8px' }
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading || code.length !== 6}
              size="large"
              sx={{
                mb: 2,
                py: 1.5,
                background: 'linear-gradient(135deg, #6366f1 0%, #ec4899 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #4f46e5 0%, #db2777 100%)'
                }
              }}
            >
              {loading ? 'Verifying...' : 'Verify Code'}
            </Button>

            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Didn't receive the code?{' '}
                <Link
                  component="button"
                  type="button"
                  onClick={handleResendCode}
                  disabled={resending}
                  sx={{
                    color: '#6366f1',
                    fontWeight: 600,
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  {resending ? 'Sending...' : 'Resend Code'}
                </Link>
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Verify2FA;
